
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import type { Cart, Customer, CustomQuery, UseUserLoginParams, UseUserRegisterParams } from '~~/types/commercetools';

type MyData = { me: { customer: Customer }};
type UserData = { user: { customer: Customer, cart: Cart }};
type CustomerData = { user: Customer };

export function useUserApi() {

  const { makeCommercetoolsRequest } = useExpondoMiddleware();

  const getCurrentUser = async (
    currentUser: Maybe<Customer>,
    customQuery?: CustomQuery,
  ): Promise<Customer> => {

    if (!currentUser) {

      const loadedUser = await load({ customQuery });

      if (!loadedUser) {

        throw new Error('User not found');
      
      }

      return loadedUser;
    
    }

    return currentUser;
  
  };

  const load = async ({ customQuery }: {
    customQuery?: CustomQuery;
  }): Promise<Customer | null> => {

    const profile = await makeCommercetoolsRequest<MyData>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.GET_ME,
      data: [{ customer: true }, customQuery],
    });

    return profile?.data?.me?.customer ?? null;
  
  };
  const logout = async (setCart: (cart: Cart | null) => void) => {

    await makeCommercetoolsRequest<null>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.CUSTOMER_SIGN_OUT,
    });
    setCart(null);
  
  };
  const updateUser = async (params: {
    currentUser: Customer,
    updatedUserData: Partial<Customer>,
    customQuery?: CustomQuery;
  }) => {

    const { currentUser, updatedUserData, customQuery } = params;
    const loadedUser = await getCurrentUser(currentUser, customQuery);
    const response = await makeCommercetoolsRequest<CustomerData>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.CUSTOMER_UPDATE_ME,
      data: [loadedUser, updatedUserData, customQuery],
    });

    return response?.data;
  
  };
  const register = async (params: UseUserRegisterParams, setCart: (cart: Cart | null) => void) => {

    const { email, password, firstName, lastName, locale, custom } = params;
    const response = await makeCommercetoolsRequest<UserData>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.CUSTOMER_SIGN_ME_UP,
      data: { email, password, firstName, lastName, locale, custom },
    });
    const customer = response?.data?.user?.customer;

    if (!customer) {

      throw new Error('Customer registration failed');
    
    }
    const cart = response?.data?.user?.cart;

    if (cart) {

      setCart(cart);
    
    }

    return customer;
  
  };
  const login = async (params: UseUserLoginParams, setCart: (cart: Cart | null) => void) => {

    const { username, password } = params;
    const customerLogin = { email: username, password };
    const response = await makeCommercetoolsRequest<UserData>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.CUSTOMER_SIGN_ME_IN,
      data: [customerLogin],
    });
    const customer = response?.data?.user?.customer;

    if (!customer) {

      throw new Error('Customer login failed');
    
    }
    const cart = response?.data?.user?.cart;

    if (cart) {

      setCart(cart);
    
    }

    return customer;
  
  };
  const changePassword = async (params: {
    currentUser: Customer,
    currentPassword: string,
    newPassword: string,
    customQuery?: CustomQuery,
  }, setCart: (cart: Cart | null) => void) => {

    const { currentUser, currentPassword, newPassword, customQuery } = params;
    const loadedUser = await getCurrentUser(currentUser, customQuery);
    const response = await makeCommercetoolsRequest<CustomerData>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.CUSTOMER_CHANGE_MY_PASSWORD,
      data: {
        version: loadedUser.version,
        currentPassword,
        newPassword,
      },
    });
    // Re-authenticate user to acquire new token
    await logout(setCart);

    if (!response?.data?.user?.email) {

      throw new Error('Password change failed');
    
    }

    return login({
      username: response.data?.user?.email,
      password: newPassword,
    }, setCart);
  
  };

  return {
    register,
    login,
    load,
    logout,
    changePassword,
    updateUser,
  };

}
